// Time off (PRD §3.13, §4.11, rev. 1.16). Everyone sees their own vacation and personal balances and
// asks for time off here. Admins (Eric or Chris for now) see every request waiting for a decision,
// everyone's balances and PTO owed (vacation only), type in payroll's balances, and set blackout dates and the hourly
// payday. Balances follow the Employee Handbook's accrual rules, in hours.
//
// The records show requests and reporting together (2026-10-29): what was asked for, what a closed time
// card recorded with no request, the adjustments where reporting differed, and each closed week as
// payroll was told it.

import { useState } from "react";
import { Badge, Body1, Button, Caption1, Card, Field, Input, Select, Subtitle2, Tab, TabList, Textarea } from "@fluentui/react-components";
import { addDays, BUCKET_LABEL, isWeekday, today, type BalanceState, type Blackout, type TimeOffBucket, type TimeOffRequest, type TimeOffWeek } from "../../../core";
import { errorText, send, useApi, type PeopleNamesResponse } from "../api";
import { DateInput } from "../DateInput";
import { money, stamp } from "../format";
import { go } from "../router";
import { ArchivedSwitch, ErrorBar, Kpi, Loading, Page, useMe, useShowArchived, Warnings } from "../ui";

interface PersonTimeOff {
  person: { id: string; name: string; employeeSince: string | null };
  asOf: string;
  eligible: boolean;
  balances: { vacation: BalanceState; personal: BalanceState } | null;
  owed: number | null;
  requests: (TimeOffRequest & { notInOutlook: string[] })[];
  payroll: { id: string; asOf: string; vacation: number; personal: number; kind: "start" | "check"; enteredBy: string; enteredAt: string }[];
  weeks: TimeOffWeek[];
}

interface Summary {
  asOf: string;
  people: {
    personId: string;
    name: string;
    archived: boolean;
    eligible: boolean;
    balances: PersonTimeOff["balances"];
    owed: number | null;
    pending: TimeOffRequest[];
    awaitingPayroll: number;
    replies: TimeOffRequest[];
    weekReplies: TimeOffWeek[];
  }[];
  payrollEmail: string;
  payrollCc: string[];
  blackouts: Blackout[];
  hourlyPayAnchor: string;
}

const h = (n: number) => `${n.toLocaleString("en-US", { maximumFractionDigits: 2 })} h`;
const total = (r: Pick<TimeOffRequest, "days">) => r.days.reduce((t, d) => t + d.hours, 0);
const span = (r: Pick<TimeOffRequest, "days">) => (r.days.length ? (r.days[0].day === r.days.at(-1)!.day ? r.days[0].day : `${r.days[0].day} to ${r.days.at(-1)!.day}`) : "");
const signed = (n: number) => (n > 0 ? `+${h(n)}` : h(n));
const KIND_SHORT = { vacation: "PTO", personal: "Sick" } as const;
/** Where a record came from (2026-10-29). */
const ORIGIN: Record<NonNullable<TimeOffRequest["origin"]>, string> = { request: "Requested", reported: "From the time card", adjustment: "Adjustment" };
const STATUS: Record<TimeOffRequest["status"], { text: string; color: "warning" | "success" | "danger" | "informative" }> = {
  requested: { text: "Waiting", color: "warning" },
  approved: { text: "Approved", color: "success" },
  declined: { text: "Declined", color: "danger" },
  cancelled: { text: "Cancelled", color: "informative" },
};

const TABS = [
  { key: "mine", label: "My time off", admin: false },
  { key: "requests", label: "Requests", admin: true },
  { key: "balances", label: "Balances", admin: true },
  { key: "settings", label: "Settings", admin: true },
] as const;

export function TimeOff({ tab }: { tab?: string }) {
  const me = useMe();
  const admin = me.can("people.edit");
  const tabs = TABS.filter((t) => admin || !t.admin);
  const current = tabs.find((t) => t.key === tab)?.key ?? (admin ? "requests" : "mine");
  return (
    <Page title="Time off">
      {tabs.length > 1 && (
        <TabList selectedValue={current} onTabSelect={(_, d) => go("timeoff", String(d.value))} aria-label="Time off sections">
          {tabs.map((t) => (
            <Tab key={t.key} value={t.key}>
              {t.label}
            </Tab>
          ))}
        </TabList>
      )}
      {current === "mine" && <PersonView />}
      {current === "requests" && <Requests />}
      {current === "balances" && <Balances />}
      {current === "settings" && <TimeOffSettings />}
    </Page>
  );
}

// --- One person: balances, a request form, their requests ----------------------------------------------

function PersonView({ personId }: { personId?: string }) {
  const data = useApi<PersonTimeOff>(`/timeoff${personId ? `?personId=${encodeURIComponent(personId)}` : ""}`);
  const d = data.data;
  if (!d) return data.loading ? <Loading /> : <ErrorBar message={data.error} />;
  if (!d.eligible) return <Body1>{d.person.name} isn't an employee, so has no time off balances. Partners and 1099 providers arrange time off directly.</Body1>;
  return (
    <>
      <BalanceTiles d={d} />
      <RequestForm personId={d.person.id} balances={d.balances} onDone={data.reload} />
      <RequestList d={d} onChange={data.reload} />
      <ClosedWeeks weeks={d.weeks} />
    </>
  );
}

function BalanceTiles({ d }: { d: PersonTimeOff }) {
  const b = d.balances!;
  const tile = (x: BalanceState, label: string) => (
    <Kpi
      label={label}
      value={h(x.available)}
      sub={
        <>
          {h(x.balance)} today{x.available !== x.balance ? `, ${h(x.balance - x.available)} booked ahead` : ""}. {h(x.perPaycheck)} a paycheck; cap {h(x.cap)}
          {x.atCap ? " (reached: accrual paused)" : ""}.
          {x.usableFrom && x.usableFrom > d.asOf ? ` Usable from ${x.usableFrom}.` : ""}
          {x.estimated && (
            <Badge appearance="tint" color="warning" size="small" className="in-effect">
              Estimated
            </Badge>
          )}
        </>
      }
    />
  );
  return (
    <>
      <div className="kpis" aria-label="Balances">
        {tile(b.vacation, "Vacation available")}
        {tile(b.personal, "Personal / sick available")}
        {d.owed !== null && <Kpi label="PTO owed" value={money(d.owed)} sub="Vacation balance at hourly pay; paid out on leaving. Sick time isn't paid out." />}
      </div>
      {(b.vacation.estimated || b.personal.estimated) && (
        <Caption1 className="block muted">
          Estimated from the handbook's accrual rules since {b.vacation.since}, less time off reported under 3SVAC and 3SSIC, until payroll's figures are entered.
        </Caption1>
      )}
    </>
  );
}

function RequestForm({ personId, balances, onDone }: { personId: string; balances: PersonTimeOff["balances"]; onDone: () => void }) {
  const [bucket, setBucket] = useState<TimeOffBucket>("vacation");
  const [from, setFrom] = useState(addDays(today(), 14));
  const [to, setTo] = useState(addDays(today(), 14));
  const [hours, setHours] = useState<Record<string, string>>({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<{ text: string; warnings: string[] }>();
  const days: string[] = [];
  for (let d = from; d <= to && days.length < 120; d = addDays(d, 1)) if (isWeekday(d)) days.push(d);
  const hoursOf = (d: string) => Number(hours[d] ?? "8");
  const sum = days.reduce((t, d) => t + (Number.isFinite(hoursOf(d)) ? hoursOf(d) : 0), 0);
  const valid = days.length > 0 && days.every((d) => hoursOf(d) > 0 && hoursOf(d) <= 24);
  const left = bucket === "unpaid" || !balances ? null : balances[bucket].available - sum;

  async function submit() {
    setBusy(true);
    setError(undefined);
    setDone(undefined);
    try {
      const r = await send<{ request: TimeOffRequest }>("POST", "/timeoff/request", { personId, bucket, days: days.map((d) => ({ day: d, hours: hoursOf(d) })), note });
      setDone({ text: `Asked for ${h(total(r.request))}, ${span(r.request)}. An admin will approve or decline it.`, warnings: r.request.warnings });
      setNote("");
      setHours({});
      onDone();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  return (
    <Card className="panel wide">
      <Subtitle2>Ask for time off</Subtitle2>
      <div className="toolbar">
        <Field label="Kind">
          <Select value={bucket} onChange={(_, x) => setBucket(x.value as TimeOffBucket)}>
            {(["vacation", "personal", "unpaid"] as const).map((b) => (
              <option key={b} value={b}>
                {BUCKET_LABEL[b]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="From">
          <DateInput type="date" value={from} onChange={(_, x) => x.value && (setFrom(x.value), x.value > to && setTo(x.value))} />
        </Field>
        <Field label="To">
          <DateInput type="date" value={to} onChange={(_, x) => x.value && setTo(x.value)} />
        </Field>
      </div>
      {days.length > 0 && (
        <div className="toolbar">
          {days.map((d) => (
            <Field key={d} label={d}>
              <Input size="small" value={hours[d] ?? "8"} onChange={(_, x) => setHours({ ...hours, [d]: x.value })} contentAfter="h" style={{ width: "6em" }} aria-label={`Hours on ${d}`} />
            </Field>
          ))}
        </div>
      )}
      <Caption1 className="block">
        {days.length} working {days.length === 1 ? "day" : "days"}, {h(sum)}
        {left !== null ? `; ${h(left)} would be left` : ""}. Partial days are fine: change the hours. Company holidays need no time off. Once approved, put a matching{" "}
        {bucket === "vacation" ? "3SVAC" : bucket === "personal" ? "3SSIC" : "3SUTO"} block on your Outlook calendar: what your closed time card shows is what's recorded and
        sent to payroll.
      </Caption1>
      <Field label="Note (optional)">
        <Textarea value={note} onChange={(_, x) => setNote(x.value)} />
      </Field>
      <div className="toolbar">
        <Button appearance="primary" disabled={busy || !valid} onClick={() => void submit()}>
          {busy ? "Sending…" : "Ask for this time off"}
        </Button>
      </div>
      <ErrorBar message={error} />
      {done && <Warnings intent="info" items={[done.text]} />}
      {done && done.warnings.length > 0 && <Warnings items={done.warnings} />}
    </Card>
  );
}

function RequestList({ d, onChange }: { d: PersonTimeOff; onChange: () => void }) {
  const me = useMe();
  const admin = me.can("people.edit");
  const [error, setError] = useState<string>();
  async function cancel(id: string) {
    setError(undefined);
    try {
      await send("POST", "/timeoff/cancel", { personId: d.person.id, requestId: id });
      onChange();
    } catch (err) {
      setError(errorText(err));
    }
  }
  return (
    <Card className="panel wide">
      <Subtitle2>Requests</Subtitle2>
      <ErrorBar message={error} />
      {!d.requests.length ? (
        <Body1 className="muted">No requests yet.</Body1>
      ) : (
        <table className="edit-table compact">
          <thead>
            <tr>
              <th>When</th>
              <th>Kind</th>
              <th>Record</th>
              <th className="num">Hours</th>
              <th>Status</th>
              <th>Notes</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {d.requests.map((r) => (
              <tr key={r.id}>
                <td>{span(r)}</td>
                <td>{BUCKET_LABEL[r.bucket]}</td>
                <td>
                  {ORIGIN[r.origin ?? "request"]}
                  {r.origin === "adjustment" && r.adjusts && (
                    <Caption1 className="block muted">to the request for {span(d.requests.find((x) => x.id === r.adjusts) ?? { days: [] }) || "a removed request"}</Caption1>
                  )}
                </td>
                <td className="num">
                  {r.origin === "adjustment" ? signed(total(r)) : h(total(r))}
                  {r.origin === "adjustment" &&
                    r.days.map((x) => (
                      <Caption1 key={x.day} className="block muted">
                        {x.day}: {signed(x.hours)}
                      </Caption1>
                    ))}
                </td>
                <td>
                  <Badge appearance="tint" color={STATUS[r.status].color}>
                    {STATUS[r.status].text}
                  </Badge>
                  {r.decidedBy && r.status !== "requested" && (
                    <Caption1 className="block muted">
                      {r.origin === "reported" || r.origin === "adjustment" ? "Recorded" : r.decidedBy}, {stamp(r.decidedAt!)}
                    </Caption1>
                  )}
                  {r.status === "approved" && r.payroll && (
                    <Caption1 className="block muted">{r.payroll.confirmedAt ? `Payroll confirmed ${stamp(r.payroll.confirmedAt)}` : "Waiting for payroll to confirm"}</Caption1>
                  )}
                </td>
                <td>
                  {r.note && <Caption1 className="block">{r.note}</Caption1>}
                  {r.decisionNote && <Caption1 className="block">Decision: {r.decisionNote}</Caption1>}
                  {r.notInOutlook.length > 0 && <Caption1 className="block warn-text">Not on the calendar yet: {r.notInOutlook.join(", ")}</Caption1>}
                </td>
                <td>
                  {(r.status === "requested" || (admin && r.status === "approved")) && (
                    <Button size="small" appearance="subtle" onClick={() => void cancel(r.id)}>
                      {r.status === "requested" ? "Withdraw" : "Cancel"}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/** Each closed week as recorded from the time card, and what payroll was told (2026-10-29). */
function ClosedWeeks({ weeks }: { weeks: TimeOffWeek[] }) {
  const withTime = weeks.filter((w) => w.lines.length);
  if (!withTime.length) return null;
  return (
    <Card className="panel wide">
      <Subtitle2>Reported from closed time cards</Subtitle2>
      <Caption1 className="block">What each closed week's time card showed: 8 h or more on a day, or an all-day event, counts as 8 h. Payroll is emailed once the time card closes.</Caption1>
      <table className="edit-table compact">
        <thead>
          <tr>
            <th>Week of</th>
            <th>Days</th>
            <th>Payroll</th>
          </tr>
        </thead>
        <tbody>
          {withTime.map((w) => (
            <tr key={w.monday}>
              <td>{w.monday}</td>
              <td>
                {w.lines.map((l) => (
                  <Caption1 key={`${l.day}${l.bucket}`} className="block">
                    {l.day}: {KIND_SHORT[l.bucket]} {h(l.hours)}
                    {l.requested ? "" : ", not requested in advance"}
                  </Caption1>
                ))}
              </td>
              <td>
                <Caption1 className="block muted">
                  {!w.payroll
                    ? "Not on payroll"
                    : w.payroll.confirmedAt
                      ? `Confirmed ${stamp(w.payroll.confirmedAt)}`
                      : w.payroll.emailedAt
                        ? `Emailed ${stamp(w.payroll.emailedAt)}, waiting for confirmation`
                        : "Waiting to send"}
                </Caption1>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// --- Admins: requests waiting, balances, settings -------------------------------------------------------

function Requests() {
  const data = useApi<Summary>("/timeoff/summary");
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState<string>();
  const d = data.data;
  if (!d) return data.loading ? <Loading /> : <ErrorBar message={data.error} />;
  const waiting = d.people.flatMap((p) => p.pending.map((r) => ({ p, r }))).sort((a, b) => a.r.days[0].day.localeCompare(b.r.days[0].day));

  async function decide(personId: string, r: TimeOffRequest, decision: "approve" | "decline") {
    setBusy(r.id);
    setError(undefined);
    setDone(undefined);
    try {
      const res = await send<{ request: TimeOffRequest }>("POST", "/timeoff/decide", { personId, requestId: r.id, decision, note: notes[r.id] ?? "" });
      const l = res.request.ledger;
      setDone(
        decision === "approve"
          ? `Approved ${h(total(r))}, ${span(r)}.${l ? ` ${h(l.before)} available before, ${h(l.after)} after.` : ""}${r.bucket === "unpaid" ? "" : " Payroll is emailed what was used once the week's time card closes."}`
          : `Declined ${span(r)}.`,
      );
      data.reload();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(null);
  }

  const replies = [
    ...d.people.flatMap((p) =>
      p.replies.map((r) => ({ p, key: r.id, about: { requestId: r.id }, what: `${span(r)}, ${h(total(r))}`, sent: r.ledger ? `sent: ${h(r.ledger.before)} → ${h(r.ledger.after)}` : "", payroll: r.payroll })),
    ),
    ...d.people.flatMap((p) =>
      p.weekReplies.map((w) => ({
        p,
        key: `${p.personId}${w.monday}`,
        about: { monday: w.monday },
        what: `Week of ${w.monday}`,
        sent: w.lines.map((l) => `${l.day} ${KIND_SHORT[l.bucket]} ${h(l.hours)}`).join(", "),
        payroll: w.payroll,
      })),
    ),
  ];
  async function settleReply(personId: string, about: { requestId: string } | { monday: string }, action: "confirm" | "dismiss") {
    setError(undefined);
    try {
      await send("POST", "/timeoff/payroll-review", { personId, ...about, action });
      data.reload();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <>
      <ErrorBar message={error} />
      {done && <Warnings intent="info" items={[done]} />}
      <MailStatus payrollEmail={d.payrollEmail} cc={d.payrollCc} onRun={data.reload} />
      {replies.length > 0 && (
        <Card className="panel wide">
          <Subtitle2>Replies from payroll to review</Subtitle2>
          <Caption1 className="block">The app closes a payroll step on a plain confirmation. These said something else, so they need a person.</Caption1>
          <table className="edit-table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Time off</th>
                <th>Reply</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {replies.map(({ p, key, about, what, sent, payroll }) => (
                <tr key={key}>
                  <td>{p.name}</td>
                  <td>
                    {what}
                    {sent && <Caption1 className="block muted">{sent}</Caption1>}
                  </td>
                  <td>
                    <Caption1 className="block" style={{ whiteSpace: "pre-wrap" }}>
                      {payroll?.reply}
                    </Caption1>
                    <Caption1 className="block muted">
                      {payroll?.replyFrom}
                      {payroll?.replyAt ? `, ${stamp(payroll.replyAt)}` : ""}
                    </Caption1>
                  </td>
                  <td>
                    <div className="toolbar">
                      <Button size="small" appearance="primary" onClick={() => void settleReply(p.personId, about, "confirm")}>
                        Mark entered in payroll
                      </Button>
                      <Button size="small" onClick={() => void settleReply(p.personId, about, "dismiss")} title="Keep waiting for another reply">
                        Keep waiting
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Card className="panel wide">
        <Subtitle2>Waiting for a decision</Subtitle2>
        {!waiting.length ? (
          <Body1 className="muted">Nothing waiting.</Body1>
        ) : (
          <table className="edit-table">
            <thead>
              <tr>
                <th>Person</th>
                <th>When</th>
                <th>Kind</th>
                <th className="num">Hours</th>
                <th className="num">Available</th>
                <th>Check</th>
                <th>Note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {waiting.map(({ p, r }) => (
                <tr key={r.id}>
                  <td>{p.name}</td>
                  <td>
                    {span(r)}
                    <Caption1 className="block muted">asked {stamp(r.requestedAt)}</Caption1>
                  </td>
                  <td>{BUCKET_LABEL[r.bucket]}</td>
                  <td className="num">{h(total(r))}</td>
                  <td className="num">{r.bucket === "unpaid" || !p.balances ? "—" : h(p.balances[r.bucket].available)}</td>
                  <td>
                    {r.note && <Caption1 className="block">“{r.note}”</Caption1>}
                    {r.warnings.map((w) => (
                      <Caption1 key={w} className="block warn-text">
                        {w}
                      </Caption1>
                    ))}
                  </td>
                  <td>
                    <Input size="small" placeholder="Optional" value={notes[r.id] ?? ""} onChange={(_, x) => setNotes({ ...notes, [r.id]: x.value })} aria-label="Decision note" />
                  </td>
                  <td>
                    <div className="toolbar">
                      <Button size="small" appearance="primary" disabled={busy === r.id} onClick={() => void decide(p.personId, r, "approve")}>
                        Approve
                      </Button>
                      <Button size="small" disabled={busy === r.id} onClick={() => void decide(p.personId, r, "decline")}>
                        Decline
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/** The outbox and the last read of replies (AM). */
function MailStatus({ payrollEmail, cc, onRun }: { payrollEmail: string; cc: string[]; onRun: () => void }) {
  const status = useApi<{ queued: number; lastResult: string | null; sent: number; lastReadAt: string | null; lastReadResult: string | null }>("/mail/status");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const s = status.data;
  if (!s) return null;
  async function run() {
    setBusy(true);
    setError(undefined);
    try {
      await send("POST", "/mail/run", {});
      status.reload();
      onRun();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }
  const denied = s.lastResult === "denied" || s.lastReadResult === "denied";
  return (
    <>
      <div className="toolbar">
        <Caption1>
          Once a week's time card closes, each employee's PTO and sick time is emailed to {payrollEmail}
          {cc.length ? ` (copy to ${cc.join(", ")})` : ""} from assistant@. {s.sent} sent{s.queued ? `, ${s.queued} waiting to send` : ""}.
          {s.lastReadAt ? ` Replies last checked ${stamp(s.lastReadAt)}.` : ""}
        </Caption1>
        <Button size="small" appearance="subtle" disabled={busy} onClick={() => void run()}>
          {busy ? "Working…" : "Record closed weeks, send and check replies now"}
        </Button>
      </div>
      {denied && (
        <Warnings items={["Exchange isn't letting the app use the assistant@ mailbox yet. Run infra/p0/8-Grant-AssistantMail.ps1; emails wait until then, and replies aren't read."]} />
      )}
      <ErrorBar message={error} />
    </>
  );
}

function Balances() {
  const data = useApi<Summary>("/timeoff/summary");
  const people = useApi<PeopleNamesResponse>("/people?view=names");
  const [open, setOpen] = useState<string | null>(null);
  const [entering, setEntering] = useState<{ personId: string; name: string } | null>(null);
  const [showArchived, setShowArchived] = useShowArchived("timeoff");
  const d = data.data;
  if (!d) return data.loading ? <Loading /> : <ErrorBar message={data.error ?? people.error} />;
  const owed = d.people.reduce((t, p) => t + (p.owed ?? 0), 0);
  const showOwed = d.people.some((p) => p.owed !== null);
  return (
    <>
      {showOwed && (
        <div className="kpis">
          <Kpi label="PTO owed, everyone" value={money(owed)} sub="Vacation balances at hourly pay (salary ÷ 2,080); paid out on leaving. Personal / sick time isn't paid out." />
        </div>
      )}
      <Card className="panel wide">
        <div className="panel-head">
          <Subtitle2>Balances, {d.asOf}</Subtitle2>
          <ArchivedSwitch count={d.people.filter((p) => p.archived && p.eligible).length} on={showArchived} onChange={setShowArchived} />
        </div>
        <Caption1 className="block">
          Available is today's balance less time off already booked ahead. Enter payroll's balances after each payroll; the first entry replaces the estimate.
        </Caption1>
        <table className="edit-table">
          <thead>
            <tr>
              <th>Person</th>
              <th className="num">Vacation</th>
              <th className="num">Personal</th>
              {showOwed && <th className="num">PTO owed</th>}
              <th>Payroll</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {d.people
              .filter((p) => p.eligible && (showArchived || !p.archived))
              .map((p) => (
                <tr key={p.personId} className={open === p.personId ? "selected" : undefined}>
                  <td>
                    <button type="button" className="link-button" onClick={() => setOpen(open === p.personId ? null : p.personId)}>
                      {p.name}
                    </button>
                  </td>
                  <td className="num">
                    {h(p.balances!.vacation.available)}
                    {p.balances!.vacation.estimated && (
                      <Badge appearance="tint" color="warning" size="small" className="in-effect">
                        Est.
                      </Badge>
                    )}
                  </td>
                  <td className="num">{h(p.balances!.personal.available)}</td>
                  {showOwed && <td className="num">{p.owed === null ? "—" : money(p.owed)}</td>}
                  <td>{p.awaitingPayroll ? <Caption1>{p.awaitingPayroll} sent, not yet confirmed</Caption1> : <Caption1 className="muted">Up to date</Caption1>}</td>
                  <td>
                    <Button size="small" onClick={() => setEntering({ personId: p.personId, name: p.name })}>
                      Enter payroll's balance…
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {d.people.some((p) => !p.eligible) && (
          <Caption1 className="muted">Not shown: {d.people.filter((p) => !p.eligible).map((p) => p.name).join(", ")} (partner or 1099, no balances).</Caption1>
        )}
      </Card>
      {entering && (
        <PayrollBalance
          {...entering}
          onClose={() => setEntering(null)}
          onSaved={() => {
            setEntering(null);
            data.reload();
          }}
        />
      )}
      {open && <PersonView key={open} personId={open} />}
    </>
  );
}

function PayrollBalance({ personId, name, onClose, onSaved }: { personId: string; name: string; onClose: () => void; onSaved: () => void }) {
  const [asOf, setAsOf] = useState(today());
  const [vacation, setVacation] = useState("");
  const [personal, setPersonal] = useState("");
  const [kind, setKind] = useState<"start" | "check">("start");
  const [error, setError] = useState<string>();
  const valid = vacation !== "" && personal !== "" && Number.isFinite(Number(vacation)) && Number.isFinite(Number(personal));
  async function save() {
    setError(undefined);
    try {
      await send("POST", "/timeoff/balance", { personId, asOf, vacation: Number(vacation), personal: Number(personal), kind });
      onSaved();
    } catch (err) {
      setError(errorText(err));
    }
  }
  return (
    <Card className="panel wide">
      <Subtitle2>Payroll's balance: {name}</Subtitle2>
      <div className="toolbar">
        <Field label="As of (a payroll date)">
          <DateInput type="date" value={asOf} onChange={(_, x) => x.value && setAsOf(x.value)} />
        </Field>
        <Field label="Vacation, hours">
          <Input value={vacation} onChange={(_, x) => setVacation(x.value)} contentAfter="h" />
        </Field>
        <Field label="Personal / sick, hours">
          <Input value={personal} onChange={(_, x) => setPersonal(x.value)} contentAfter="h" />
        </Field>
        <Field label="Use it to">
          <Select value={kind} onChange={(_, x) => setKind(x.value as "start" | "check")}>
            <option value="start">Set the balance from this date</option>
            <option value="check">Only compare with the app's</option>
          </Select>
        </Field>
      </div>
      <ErrorBar message={error} />
      <div className="toolbar">
        <Button appearance="primary" disabled={!valid} onClick={() => void save()}>
          Save
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </div>
    </Card>
  );
}

function TimeOffSettings() {
  const ref = useApi<{
    settings: {
      _etag?: string | null;
      hourlyPayAnchor: string;
      timeOffBlackouts: Blackout[];
      payrollEmail: string;
      payrollName: string;
      timeOffPayrollEmail: string;
      timeOffPayrollName: string;
      timeOffPayrollCc: string[];
    };
  }>("/reference");
  const [contact, setContact] = useState<{ email: string; name: string } | null>(null);
  const [provider, setProvider] = useState<{ email: string; name: string; cc: string } | null>(null);
  const [rows, setRows] = useState<Blackout[] | null>(null);
  const [anchor, setAnchor] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);
  const s = ref.data?.settings;
  if (!s) return ref.loading ? <Loading /> : <ErrorBar message={ref.error} />;
  const list = rows ?? s.timeOffBlackouts ?? [];
  const day = anchor ?? s.hourlyPayAnchor;
  const who = contact ?? { email: s.payrollEmail, name: s.payrollName };
  const pay = provider ?? { email: s.timeOffPayrollEmail, name: s.timeOffPayrollName, cc: s.timeOffPayrollCc.join(", ") };
  async function save() {
    setError(undefined);
    setSaved(false);
    try {
      await send("PUT", "/reference/settings", {
        etag: s!._etag ?? null,
        settings: {
          timeOffBlackouts: list,
          hourlyPayAnchor: day,
          payrollEmail: who.email,
          payrollName: who.name,
          timeOffPayrollEmail: pay.email,
          timeOffPayrollName: pay.name,
          timeOffPayrollCc: pay.cc.split(/[,;\s]+/).filter(Boolean),
        },
      });
      setSaved(true);
      setRows(null);
      setAnchor(null);
      setContact(null);
      setProvider(null);
      ref.reload();
    } catch (err) {
      setError(errorText(err));
    }
  }
  return (
    <Card className="panel wide">
      <Subtitle2>Blackout dates</Subtitle2>
      <Caption1 className="block">Requests that touch these dates show a warning. They aren't blocked.</Caption1>
      {list.map((b, i) => (
        <div key={i} className="toolbar">
          <DateInput size="small" type="date" value={b.from} onChange={(_, x) => setRows(list.map((y, j) => (j === i ? { ...y, from: x.value } : y)))} aria-label="From" />
          <DateInput size="small" type="date" value={b.to} onChange={(_, x) => setRows(list.map((y, j) => (j === i ? { ...y, to: x.value } : y)))} aria-label="To" />
          <Input size="small" placeholder="Why (optional)" value={b.note ?? ""} onChange={(_, x) => setRows(list.map((y, j) => (j === i ? { ...y, note: x.value } : y)))} aria-label="Note" />
          <Button size="small" appearance="subtle" onClick={() => setRows(list.filter((_, j) => j !== i))}>
            Remove
          </Button>
        </div>
      ))}
      <div className="toolbar">
        <Button size="small" onClick={() => setRows([...list, { from: today(), to: today() }])}>
          Add a blackout
        </Button>
      </div>
      <Subtitle2>Hourly payday</Subtitle2>
      <Caption1 className="block">
        Hourly employees are paid every other Friday, and accrue 3.0769 h of each balance per paycheck. Enter any one real payday so the app counts the
        same Fridays as payroll.
      </Caption1>
      <Field label="A known hourly payday (a Friday)">
        <DateInput type="date" value={day} onChange={(_, x) => x.value && setAnchor(x.value)} />
      </Field>
      <Subtitle2>Payroll provider</Subtitle2>
      <Caption1 className="block">
        Once a week's time card closes, each employee's PTO and sick time for it is emailed here from assistant@, with copies. Their reply closes the step. Partners and
        1099s are never included.
      </Caption1>
      <div className="toolbar">
        <Field label="Name">
          <Input value={pay.name} onChange={(_, x) => setProvider({ ...pay, name: x.value })} />
        </Field>
        <Field label="Email">
          <Input value={pay.email} onChange={(_, x) => setProvider({ ...pay, email: x.value })} style={{ minWidth: "18em" }} />
        </Field>
        <Field label="Copy to">
          <Input value={pay.cc} onChange={(_, x) => setProvider({ ...pay, cc: x.value })} style={{ minWidth: "28em" }} />
        </Field>
      </div>
      <Subtitle2>Expense reimbursements</Subtitle2>
      <Caption1 className="block">Approved expense reports are emailed here from assistant@ to pay from QuickBooks, and their reply closes the report.</Caption1>
      <div className="toolbar">
        <Field label="Name">
          <Input value={who.name} onChange={(_, x) => setContact({ ...who, name: x.value })} />
        </Field>
        <Field label="Email">
          <Input value={who.email} onChange={(_, x) => setContact({ ...who, email: x.value })} />
        </Field>
      </div>
      <ErrorBar message={error} />
      {saved && <Warnings intent="info" items={["Saved."]} />}
      <div className="toolbar">
        <Button appearance="primary" disabled={rows === null && anchor === null && contact === null && provider === null} onClick={() => void save()}>
          Save
        </Button>
      </div>
    </Card>
  );
}
